// PounceLeadsPage.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Pounce ki saari leads ka page (/leads) — Pounce agent ke "New Leads" tile se
// khulta hai. Upar search, phir status ke tabs, phir har lead ka card.
//
// Data: GET /pounce/leads, socket ke "pounce_lead_updated" pe taaza.
// ⚠️ Abhi NAKLI data: USE_STATIC_LEADS = true hai, to page staticLeads.js ki
// list dikhata hai, asli API nahi. Asli data ke liye flag false karo — tab wahi
// GET /pounce/leads chalta hai jo agent page ka Leads section leta hai, aur
// 15 second pe taaza hota hai.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useState } from "react";
import OutboundCallerBrand from "../OutboundCallerBrand/OutboundCallerBrand";
import { useSearchParams } from "react-router-dom";
import { getPounceLeads } from "../../Store/apiStore";
import HeaderBar from "../HeaderBar/HeaderBar";
import styles from "./PounceLeadsPage.module.css";
import PounceLeadsTabs from "./PounceLeadsTabs/PounceLeadsTabs";
import PounceLeadCard from "./PounceLeadCard/PounceLeadCard";
import PounceLeadCardSkeleton from "./PounceLeadCardSkeleton/PounceLeadCardSkeleton";
import PounceFooterNav from "../PounceAgentDetail/PounceFooterNav/PounceFooterNav";
import { usePounceLeadEvents } from "./pounceLeadShared";
import PounceToggle from "./PounceToggle/PounceToggle";

// Har tab kin statuses ko dikhata hai. "Qualified" = booked + hot lead.
const TABS = [
  { key: "all", label: "All", match: () => true },
  { key: "new", label: "New", match: (lead) => lead.status === "new" },
  { key: "calling", label: "Calling", match: (lead) => lead.status === "calling" },
  {
    key: "qualified",
    label: "Qualified",
    match: (lead) => lead.status === "booked" || lead.status === "qualified_no_book",
  },
];

const matchesSearch = (lead, query) => {
  if (!query) return true;
  // Phone me se space/brackets hata kar bhi dekho: "415 555" → "+1 (415) 555-…".
  const digits = query.replace(/\D/g, "");
  return (
    [lead.name, lead.company, lead.email, lead.phone].some((v) =>
      String(v || "").toLowerCase().includes(query),
    ) ||
    (digits.length > 0 && String(lead.phone || "").replace(/\D/g, "").includes(digits))
  );
};

const PounceLeadsPage = () => {
  const [leads, setLeads] = useState(null); // null = pehli baar load ho raha hai
  const [failed, setFailed] = useState(false);
  // ?tab=qualified jaise link (agent page ke tiles) seedha us tab pe kholte hain.
  const [params] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "all"
  );
  const [search, setSearch] = useState("");

  // Footer ke "Home" ke liye: jis agent se user yahan aaya. Dashboard aur
  // agent page dono ise SelectAgentId me rakhte hain.
  const agentId =
    sessionStorage.getItem("SelectAgentId") || localStorage.getItem("SelectAgentId") || "";

  const load = useCallback(() => {

    getPounceLeads({ limit: 100 })
      .then((rows) => {
        setLeads(rows);
        setFailed(false);
      })
      // Poll fail ho to purani list dikhti rahe; pehli baar hi fail ho to error.
      // 403 = team member ke paas call logs ki permission nahi (server tay karta hai).
      .catch((err) => setFailed(err?.response?.status === 403 ? "forbidden" : true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Live: koi bhi lead badle (socket) → list taaza.
  usePounceLeadEvents(load);
  const query = search.trim().toLowerCase();

  // Search pehle, tab baad me — to tabs ki ginti bhi search ke hisaab se hoti hai.
  const searched = useMemo(
    () => (leads || []).filter((lead) => matchesSearch(lead, query)),
    [leads, query],
  );

  const tabs = TABS.map((tab) => ({
    key: tab.key,
    label: `${tab.label} (${searched.filter(tab.match).length})`,
  }));

  const visible = searched.filter(TABS.find((tab) => tab.key === activeTab).match);

  return (
    <div className={styles.page}>
      {/* Header + search ek saath upar chipke rehte hain (.stickyTop). */}
      <div className={styles.stickyTop}>
      {/* Logo `subtitle` me: HeaderBar ka `title` sirf string leta hai. */}
      <div className={styles.headerSlot}>
        <HeaderBar
          title=""
          subtitle={<OutboundCallerBrand />}
          rightIcon={<PounceToggle onChange={load} />}
        />
      </div>

      <label className={styles.search}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          type="search"
          className={styles.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, phone, or company..."
          aria-label="Search leads"
        />
      </label>
      </div>

      {/* Tabs page ke side padding se bahar nikalte hain, taaki neeche ki
          line screen ke dono kinaron ko chhue. */}
      <div className={styles.tabsBleed}>
        <PounceLeadsTabs tabs={tabs} active={activeTab} onChange={setActiveTab} />
      </div>

      {leads === null ? (
        failed ? (
          <div className={styles.state}>
            <p className={styles.stateTitle}>
              {failed === "forbidden" ? "You don't have access to leads" : "We couldn't load your leads"}
            </p>
            {failed === "forbidden" && (
              <p className={styles.stateText}>Ask the account owner to give you call log access.</p>
            )}
            <button type="button" className={styles.retryBtn} onClick={load}>
              Try again
            </button>
          </div>
        ) : (
          <PounceLeadCardSkeleton />
        )
      ) : visible.length === 0 ? (
        <div className={styles.state}>
          <p className={styles.stateTitle}>{query ? "No leads match your search" : "No leads here yet"}</p>
          <p className={styles.stateText}>
            {query
              ? "Try a different name, phone number or company."
              : "When a lead comes in, Outbound Caller calls it within seconds and it shows up here."}
          </p>
        </div>
      ) : (
        <ul className={styles.list}>
          {visible.map((lead) => (
            <PounceLeadCard key={lead.id} lead={lead} />
          ))}
        </ul>
      )}

      {/* Wahi footer jo Pounce agent page pe hai; yahan "Leads" chuna hua. */}
      <PounceFooterNav agentId={agentId} active="leads" />
    </div>
  );
};

export default PounceLeadsPage;
